"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Building2, UserCheck, Landmark, FileSearch, FileText, Scale, CircleCheck as CheckCircle2, ArrowRight, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
export default function Accompaniment() {
  const shouldReduceMotion = useReducedMotion();
  const sectors = [{
    id: "empresas",
    title: "Empresas",
    subtitle: "Cumplimiento preventivo y gobierno corporativo",
    description: "Blindaje contractual, auditorías de legalidad, prevención de contingencias laborales y fiscales, y estructuración de acuerdos entre socios.",
    icon: Building2
  }, {
    id: "particulares",
    title: "Particulares",
    subtitle: "Protección patrimonial y defensa de derechos",
    description: "Asesoría puntual en compraventa de activos, regularización de predios agrarios, sucesiones, convenios civiles y salvaguarda patrimonial.",
    icon: UserCheck
  }, {
    id: "entes-publicos",
    title: "Entes públicos",
    subtitle: "Opiniones técnicas y resolución administrativa",
    description: "Emisión de dictámenes técnicos, soporte legal en contrataciones y licitaciones, solventación ante órganos fiscalizadores y juicio contencioso.",
    icon: Landmark
  }];
  const lifecycle = [{
    step: "01",
    title: "Diagnóstico preliminar",
    description: "Evaluación documental inicial para medir viabilidad, riesgos procesales y alcance legal.",
    icon: FileSearch
  }, {
    step: "02",
    title: "Estructuración y redacción",
    description: "Diseño de la estrategia procesal, redacción de contratos, recursos o medios de defensa.",
    icon: FileText
  }, {
    step: "03",
    title: "Representación activa",
    description: "Comparecencia directa ante tribunales administrativos, agrarios, civiles o entidades regulatorias.",
    icon: Scale
  }, {
    step: "04",
    title: "Resolución definitiva",
    description: "Ejecución de acuerdos, cumplimiento de sentencias y blindaje preventivo para evitar contingencias futuras.",
    icon: CheckCircle2
  }];
  const fadeUpVariant = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const containerVariant = {
    hidden: {
      opacity: 1
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  return <section id="acompañamiento-jurídico" data-nav="light" className="relative bg-card py-20 lg:py-28 border-t border-border overflow-hidden">
      <div className="container mx-auto px-6 md:px-10 lg:px-16 max-w-7xl">
        {/* Header Block */}
        <motion.div initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} variants={fadeUpVariant} className="max-w-3xl mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-sm border border-border bg-background">
            <ShieldCheck className="size-4 text-foreground" />
            <span className="font-serif text-xs font-bold tracking-widest text-foreground uppercase">
              Área jurídica · ASV Consultores
            </span>
          </div>
          
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-wide uppercase text-foreground leading-tight mb-4">
            Acompañamiento jurídico institucional
          </h2>

          {/* ASV Gold Hairline Motif */}
          <div className="w-12 h-[1.5px] bg-foreground/30 mb-6" />

          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Representamos a empresas, ciudadanos y dependencias de gobierno en sus procesos administrativos, fiscales y patrimoniales con un criterio técnico estricto y visión preventiva.
          </p>
        </motion.div>

        {/* Two-Column Editorial Architecture */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {/* Left Column: Client Sectors & Representation Lifecycle */}
          <div className="lg:col-span-7 space-y-14">
            
            {/* Sectors Served */}
            <div>
              <div className="mb-6">
                <span className="font-serif text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Sectores de atención especializada
                </span>
                <h3 className="font-serif text-xl sm:text-2xl font-semibold uppercase text-foreground mt-1">
                  Soluciones diseñadas para cada naturaleza jurídica
                </h3>
              </div>

              <motion.div variants={containerVariant} initial="hidden" whileInView="show" viewport={{
              once: true,
              amount: 0.1
            }} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {sectors.map((sector, i) => {
                const SectorIcon = sector.icon;
                return <motion.div key={sector.id} variants={fadeUpVariant} data-index={i}>
                      <Card className="h-full rounded-sm border border-border bg-background shadow-xs hover:border-foreground/40 transition-[border-color,box-shadow] duration-250 flex flex-col justify-between">
                        <CardHeader className="p-5 pb-3">
                          <div className="w-10 h-10 rounded-sm border border-border bg-card flex items-center justify-center mb-3">
                            <SectorIcon className="size-5 text-foreground" />
                          </div>
                          <CardTitle className="font-serif text-base font-semibold uppercase text-foreground tracking-wide">
                            {sector.title}
                          </CardTitle>
                          <CardDescription className="text-xs text-muted-foreground font-medium mt-1 leading-snug">
                            {sector.subtitle}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="p-5 pt-0">
                          <p className="text-xs text-foreground/80 leading-relaxed">
                            {sector.description}
                          </p>
                        </CardContent>
                      </Card>
                    </motion.div>;
              })}
              </motion.div>
            </div>

            {/* Accompanying Lifecycle */}
            <div>
              <div className="mb-6">
                <span className="font-serif text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Ciclo de representación
                </span>
                <h3 className="font-serif text-xl sm:text-2xl font-semibold uppercase text-foreground mt-1">
                  De la valoración técnica a la resolución firme
                </h3>
              </div>

              <motion.div variants={containerVariant} initial="hidden" whileInView="show" viewport={{
              once: true,
              amount: 0.1
            }} className="space-y-3">
                {lifecycle.map((stepItem, i) => {
                const StepIcon = stepItem.icon;
                return <motion.div key={stepItem.step} variants={fadeUpVariant} data-index={i} className="p-4 rounded-sm border border-border bg-background flex flex-col sm:flex-row sm:items-center justify-between gap-4 transition-[border-color] duration-200 hover:border-foreground/30">
                      <div className="flex items-start sm:items-center gap-4">
                        <div className="w-9 h-9 shrink-0 rounded-sm border border-border bg-card flex items-center justify-center text-xs font-serif font-bold text-foreground">
                          {stepItem.step}
                        </div>
                        <div>
                          <h4 className="font-serif text-sm font-semibold uppercase tracking-wide text-foreground">
                            {stepItem.title}
                          </h4>
                          <p className="text-xs text-muted-foreground mt-0.5 max-w-lg leading-relaxed">
                            {stepItem.description}
                          </p>
                        </div>
                      </div>
                      <div className="hidden sm:flex shrink-0">
                        <StepIcon className="size-4 text-muted-foreground" />
                      </div>
                    </motion.div>;
              })}
              </motion.div>
            </div>

            {/* Call to action action row */}
            <motion.div initial="hidden" whileInView="show" viewport={{
            once: true
          }} variants={fadeUpVariant} className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <Button asChild className="bg-primary text-primary-foreground border border-border hover:bg-primary/90 font-serif uppercase text-xs tracking-wider font-semibold rounded-xs h-11 px-6 shadow-xs">
                <Link to="/ContactPage">
                  Solicitar asesoría legal
                  <ArrowRight className="size-3.5 ml-1" />
                </Link>
              </Button>

              <Button asChild variant="outline" className="border-foreground/20 text-foreground hover:bg-muted font-serif uppercase text-xs tracking-wider font-semibold rounded-xs h-11 px-6">
                <Link to="/OnlineConsultingPage">
                  Consultoría en línea
                </Link>
              </Button>
            </motion.div>

          </div>

          {/* Right Column: Senior Attorney Legal Meeting Room Visual & Institutional Card */}
          <div className="lg:col-span-5 space-y-6">
            <motion.div initial="hidden" whileInView="show" viewport={{
            once: true,
            amount: 0.2
          }} variants={fadeUpVariant} className="relative">
              <div className="overflow-hidden rounded-xs border border-border shadow-md bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/senior_attorney_legal_consultation.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/senior_attorney_legal_consultation.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/senior_attorney_legal_consultation.png-wvc-sizes" alt="Abogado senior asesorando a clientes en una sala de juntas jurídica con acervo bibliográfico" loading="lazy" decoding="async" className="w-full h-[380px] sm:h-[460px] object-cover object-center transition-transform duration-700 hover:scale-[1.02]" />
              </div>

              {/* Editorial Caption Box */}
              <div className="mt-4 p-5 rounded-xs border border-border bg-background">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-foreground" />
                  <span className="font-serif text-[11px] font-bold uppercase tracking-widest text-foreground">
                    Supervisión especializada
                  </span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Cada expediente es revisado colegiadamente bajo la dirección de socios con amplia práctica en tribunales y administración pública, garantizando estricta confidencialidad y certeza técnica.
                </p>
                <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-xs text-foreground/80 font-medium">
                  <span>Sede Los Mochis, Sinaloa</span>
                  <span className="text-muted-foreground">Lunes a Viernes 9:00 - 18:00</span>
                </div>
              </div>
            </motion.div>

            {/* Key Practices Summary Badge Card */}
            <motion.div initial="hidden" whileInView="show" viewport={{
            once: true
          }} variants={fadeUpVariant} className="p-5 rounded-xs border border-border bg-background/60 flex items-start gap-4">
              <div className="p-2.5 rounded-xs bg-muted border border-border shrink-0">
                <Scale className="size-5 text-foreground" />
              </div>
              <div className="space-y-1">
                <h4 className="font-serif text-xs font-bold uppercase tracking-wider text-foreground">
                  Materias de práctica recurrente
                </h4>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Derecho administrativo · Fiscal y contencioso · Gobierno corporativo · Derecho agrario · Contratación y licitaciones públicas.
                </p>
              </div>
            </motion.div>
          </div>

        </div>
      </div>
    </section>;
}